/**
 * Interactive Trajectory Editor
 * Draggable spheres with axis arrows for precise control
 */

import React, { useState, useMemo, useRef, useEffect } from 'react'
import * as THREE from 'three/webgpu'
import { useThree, useFrame } from '@react-three/fiber'

// Axis arrow helper with interactive shaft
function AxisArrow({ direction, color, scale = 1, thickness = 1, onPointerDown }) {
  const [isHovered, setIsHovered] = useState(false)
  
  const geometry = useMemo(() => {
    const geo = new THREE.CylinderGeometry(0.015 * thickness, 0.015 * thickness, scale, 8)
    geo.rotateZ(-Math.PI / 2)
    geo.translate(scale / 2, 0, 0)
    return geo
  }, [scale, thickness])
  
  const coneGeometry = useMemo(() => {
    const geo = new THREE.ConeGeometry(0.04 * thickness, 0.12 * thickness, 8)
    geo.rotateZ(-Math.PI / 2)
    geo.translate(scale + 0.06 * thickness, 0, 0)
    return geo
  }, [scale, thickness])
  
  // Invisible clickable shaft (thicker for easier interaction)
  const hitGeometry = useMemo(() => {
    const geo = new THREE.CylinderGeometry(0.08, 0.08, scale, 8)
    geo.rotateZ(-Math.PI / 2)
    geo.translate(scale / 2, 0, 0)
    return geo
  }, [scale])
  
  const quaternion = useMemo(() => {
    const up = new THREE.Vector3(1, 0, 0)
    return new THREE.Quaternion().setFromUnitVectors(up, direction.clone().normalize())
  }, [direction])
  
  // Create materials once
  const material = useMemo(() => new THREE.MeshBasicMaterial(), [])
  
  // Cleanup geometries and material on unmount
  useEffect(() => {
    return () => {
      geometry.dispose()
      coneGeometry.dispose()
      hitGeometry.dispose()
      material.dispose()
    }
  }, [geometry, coneGeometry, hitGeometry, material])
  
  const displayColor = isHovered ? '#ffffff' : color
  
  // Update material color when it changes
  useEffect(() => {
    material.color.set(displayColor)
  }, [material, displayColor])
  
  return (
    <group quaternion={quaternion}>
      {/* Invisible hit target */}
      <mesh 
        geometry={hitGeometry}
        visible={false}
        onPointerDown={onPointerDown}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
      />
      {/* Visible arrow */}
      <mesh geometry={geometry} material={material} raycast={() => null} />
      <mesh geometry={coneGeometry} material={material} raycast={() => null} />
    </group>
  )
}

// Draggable Bézier handle with axis gizmos
function DraggableHandle({ position, controlPointPos, index, handleType, onDrag, onDragEnd, color, locked, showGizmos }) {
  const meshRef = useRef()
  const [isDragging, setIsDragging] = useState(false)
  const [dragAxis, setDragAxis] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const { camera, gl } = useThree()
  const raycaster = useMemo(() => new THREE.Raycaster(), [])
  
  // Create sphere geometry once
  const sphereGeometry = useMemo(() => {
    return new THREE.SphereGeometry(0.03, 16, 16)
  }, [])
  
  // Create material once
  const material = useMemo(() => new THREE.MeshBasicMaterial({ transparent: true }), [])
  
  // Cleanup geometry and material on unmount
  useEffect(() => {
    return () => {
      sphereGeometry.dispose()
      material.dispose()
    }
  }, [sphereGeometry, material])
  
  // Update material properties when they change
  useEffect(() => {
    material.color.set(color)
    material.opacity = isHovered ? 1 : 0.8
  }, [material, color, isHovered])
  
  const dragAxisRef = useRef(null)
  const positionRef = useRef(position)
  const controlPosRef = useRef(controlPointPos)
  const dragOffsetRef = useRef(new THREE.Vector3())
  
  React.useEffect(() => {
    positionRef.current = position
    controlPosRef.current = controlPointPos
  }, [position, controlPointPos])
  
  // Absolute world position of handle
  const worldPos = useMemo(() => [
    controlPointPos[0] + position[0],
    controlPointPos[1] + position[1],
    controlPointPos[2] + position[2]
  ], [controlPointPos, position])
  
  const handleGlobalPointerMove = React.useCallback((e) => {
    const rect = gl.domElement.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    
    raycaster.setFromCamera({ x, y }, camera)
    const ray = raycaster.ray
    const controlPos = controlPosRef.current
    const currentRel = positionRef.current
    const offset = dragOffsetRef.current
    
    // Current absolute position
    const absPos = [
      controlPos[0] + currentRel[0],
      controlPos[1] + currentRel[1],
      controlPos[2] + currentRel[2]
    ]
    
    if (dragAxisRef.current === 'x') {
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -absPos[1])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        const newAbsX = intersection.x + offset.x
        const relativePos = [newAbsX - controlPos[0], currentRel[1], currentRel[2]]
        onDrag(index, handleType, relativePos)
      }
    } else if (dragAxisRef.current === 'y') {
      const plane = new THREE.Plane(new THREE.Vector3(1, 0, 0), -absPos[0])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        const newAbsY = intersection.y + offset.y
        const relativePos = [currentRel[0], newAbsY - controlPos[1], currentRel[2]]
        onDrag(index, handleType, relativePos)
      }
    } else if (dragAxisRef.current === 'z') {
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -absPos[1])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        const newAbsZ = intersection.z + offset.z
        const relativePos = [currentRel[0], currentRel[1], newAbsZ - controlPos[2]]
        onDrag(index, handleType, relativePos)
      }
    } else if (dragAxisRef.current === null) {
      // Free XY plane drag
      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -absPos[2])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        const newAbsX = intersection.x + offset.x
        const newAbsY = intersection.y + offset.y
        const relativePos = [
          newAbsX - controlPos[0],
          newAbsY - controlPos[1],
          currentRel[2]
        ]
        onDrag(index, handleType, relativePos)
      }
    }
  }, [camera, gl, raycaster, index, handleType, onDrag])
  
  const handleGlobalPointerUp = React.useCallback(() => {
    if (isDragging && onDragEnd) {
      onDragEnd(index)
    }
    setIsDragging(false)
    setDragAxis(null)
    dragAxisRef.current = null
    dragOffsetRef.current.set(0, 0, 0)
    if (window.orbitControlsRef) {
      window.orbitControlsRef.enabled = true
    }
    
    // Remove global listeners
    window.removeEventListener('pointermove', handleGlobalPointerMove)
    window.removeEventListener('pointerup', handleGlobalPointerUp)
  }, [isDragging, index, onDragEnd, handleGlobalPointerMove])
  
  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('pointermove', handleGlobalPointerMove)
      window.addEventListener('pointerup', handleGlobalPointerUp)
      return () => {
        window.removeEventListener('pointermove', handleGlobalPointerMove)
        window.removeEventListener('pointerup', handleGlobalPointerUp)
      }
    }
  }, [isDragging, handleGlobalPointerMove, handleGlobalPointerUp])
  
  const handleAxisPointerDown = (e, axis) => {
    e.stopPropagation()
    if (locked) return
    setIsDragging(true)
    setDragAxis(axis)
    dragAxisRef.current = axis
    
    if (window.orbitControlsRef) {
      window.orbitControlsRef.enabled = false
    }
    
    const rect = gl.domElement.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    
    raycaster.setFromCamera({ x, y }, camera)
    const ray = raycaster.ray
    const controlPos = controlPosRef.current
    const currentRel = positionRef.current
    const absPos = [
      controlPos[0] + currentRel[0],
      controlPos[1] + currentRel[1],
      controlPos[2] + currentRel[2]
    ]
    
    if (axis === 'x') {
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -absPos[1])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        dragOffsetRef.current.set(absPos[0] - intersection.x, 0, 0)
      }
    } else if (axis === 'y') {
      const plane = new THREE.Plane(new THREE.Vector3(1, 0, 0), -absPos[0])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        dragOffsetRef.current.set(0, absPos[1] - intersection.y, 0)
      }
    } else if (axis === 'z') {
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -absPos[1])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        dragOffsetRef.current.set(0, 0, absPos[2] - intersection.z)
      }
    }
  }
  
  const handleCenterPointerDown = (e) => {
    e.stopPropagation()
    if (locked) return
    setIsDragging(true)
    setDragAxis(null)
    dragAxisRef.current = null
    
    if (window.orbitControlsRef) {
      window.orbitControlsRef.enabled = false
    }
    
    const rect = gl.domElement.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    
    raycaster.setFromCamera({ x, y }, camera)
    const ray = raycaster.ray
    const controlPos = controlPosRef.current
    const currentRel = positionRef.current
    const absPos = [
      controlPos[0] + currentRel[0],
      controlPos[1] + currentRel[1],
      controlPos[2] + currentRel[2]
    ]
    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -absPos[2])
    const intersection = new THREE.Vector3()
    ray.intersectPlane(plane, intersection)
    if (intersection) {
      dragOffsetRef.current.set(absPos[0] - intersection.x, absPos[1] - intersection.y, 0)
    }
  }
  
  if (!showGizmos) return null

  return (
    <group position={worldPos}>
      {/* Center sphere */}
      <mesh
        ref={meshRef}
        geometry={sphereGeometry}
        material={material}
        onPointerDown={handleCenterPointerDown}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
      />
      
      {/* X, Y, Z axis arrows - hide when locked */}
      {!locked && showGizmos && (
        <>
          <AxisArrow 
            direction={new THREE.Vector3(1, 0, 0)} 
            color="#ff0000"
            scale={0.15}
            thickness={0.6}
            onPointerDown={(e) => handleAxisPointerDown(e, 'x')}
          />
          <AxisArrow 
            direction={new THREE.Vector3(0, 1, 0)} 
            color="#00ff00"
            scale={0.15}
            thickness={0.6}
            onPointerDown={(e) => handleAxisPointerDown(e, 'y')}
          />
          <AxisArrow 
            direction={new THREE.Vector3(0, 0, 1)} 
            color="#0000ff"
            scale={0.15}
            thickness={0.6}
            onPointerDown={(e) => handleAxisPointerDown(e, 'z')}
          />
        </>
      )}
    </group>
  )
}

// Draggable point with axis gizmos
function DraggablePoint({ position, index, onDrag, onDragEnd, color, locked, showGizmos }) {
  const meshRef = useRef()
  const groupRef = useRef()
  const [isDragging, setIsDragging] = useState(false)
  const [dragAxis, setDragAxis] = useState(null) // 'x', 'y', 'z', or null for free drag
  const [isHovered, setIsHovered] = useState(false)
  const { camera, gl } = useThree()
  const raycaster = useMemo(() => new THREE.Raycaster(), [])
  
  // Create sphere geometry once
  const sphereGeometry = useMemo(() => {
    return new THREE.SphereGeometry(0.08, 16, 16)
  }, [])
  
  // Create material once
  const material = useMemo(() => new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.9 }), [])
  
  // Cleanup geometry and material on unmount
  useEffect(() => {
    return () => {
      sphereGeometry.dispose()
      material.dispose()
    }
  }, [sphereGeometry, material])
  
  // Update material color when state changes
  useEffect(() => {
    const newColor = isDragging && dragAxis === null ? '#ffffff' : isHovered ? '#ffff00' : color
    material.color.set(newColor)
  }, [material, isDragging, dragAxis, isHovered, color])
  
  // Use refs to avoid stale closures in global event handlers
  const dragAxisRef = useRef(null)
  const positionRef = useRef(position)
  const dragOffsetRef = useRef(new THREE.Vector3())
  const wasDraggingRef = useRef(false)
  
  // Update position ref when position changes
  React.useEffect(() => {
    positionRef.current = position
  }, [position])
  
  const handleGlobalPointerMove = React.useCallback((e) => {
    if (!dragAxisRef.current && dragAxisRef.current !== null) return
    
    // Get mouse position in NDC
    const rect = gl.domElement.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    
    raycaster.setFromCamera({ x, y }, camera)
    const ray = raycaster.ray
    const pos = positionRef.current
    const offset = dragOffsetRef.current
    
    if (dragAxisRef.current === 'x') {
      // Constrain to X axis - intersect with XZ plane
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -pos[1])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        onDrag(index, [intersection.x + offset.x, pos[1], pos[2]])
      }
    } else if (dragAxisRef.current === 'y') {
      // Constrain to Y axis - intersect with YZ plane
      const plane = new THREE.Plane(new THREE.Vector3(1, 0, 0), -pos[0])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        onDrag(index, [pos[0], intersection.y + offset.y, pos[2]])
      }
    } else if (dragAxisRef.current === 'z') {
      // Constrain to Z axis - intersect with XZ plane
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -pos[1])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        onDrag(index, [pos[0], pos[1], intersection.z + offset.z])
      }
    } else if (dragAxisRef.current === null) {
      // Free XY plane drag
      const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -pos[2])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        onDrag(index, [intersection.x + offset.x, intersection.y + offset.y, pos[2]])
      }
    }
  }, [camera, gl, raycaster, onDrag, index])
  
  const handleGlobalPointerUp = React.useCallback(() => {
    if (wasDraggingRef.current && onDragEnd) {
      onDragEnd(index)
    }
    wasDraggingRef.current = false
    setIsDragging(false)
    setDragAxis(null)
    dragAxisRef.current = undefined
    dragOffsetRef.current.set(0, 0, 0)
    gl.domElement.style.cursor = 'auto'
    if (window.orbitControlsRef) {
      window.orbitControlsRef.enabled = true
    }
    
    // Remove global listeners
    window.removeEventListener('pointermove', handleGlobalPointerMove)
    window.removeEventListener('pointerup', handleGlobalPointerUp)
  }, [index, onDragEnd, gl, handleGlobalPointerMove])
  
  const handleAxisPointerDown = (e, axis) => {
    e.stopPropagation()
    if (locked) return
    
    // Calculate initial offset
    const rect = gl.domElement.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
    const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
    
    raycaster.setFromCamera({ x, y }, camera)
    const ray = raycaster.ray
    const pos = position
    
    // Calculate where the click hit relative to the point position
    if (axis === 'x') {
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -pos[1])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        dragOffsetRef.current.set(pos[0] - intersection.x, 0, 0)
      }
    } else if (axis === 'y') {
      const plane = new THREE.Plane(new THREE.Vector3(1, 0, 0), -pos[0])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        dragOffsetRef.current.set(0, pos[1] - intersection.y, 0)
      }
    } else if (axis === 'z') {
      const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -pos[1])
      const intersection = new THREE.Vector3()
      ray.intersectPlane(plane, intersection)
      if (intersection) {
        dragOffsetRef.current.set(0, 0, pos[2] - intersection.z)
      }
    }
    
    setIsDragging(true)
    setDragAxis(axis)
    dragAxisRef.current = axis
    wasDraggingRef.current = true
    gl.domElement.style.cursor = 'grabbing'
    if (window.orbitControlsRef) {
      window.orbitControlsRef.enabled = false
    }
    
    // Add global listeners
    window.addEventListener('pointermove', handleGlobalPointerMove)
    window.addEventListener('pointerup', handleGlobalPointerUp)
  }
  
  // If gizmos hidden, render nothing for the point's interactive visuals
  if (!showGizmos) return null

  return (
    <group ref={groupRef} position={position}>
      {/* Center sphere */}
      <mesh
        ref={meshRef}
        geometry={sphereGeometry}
        onPointerDown={(e) => {
          e.stopPropagation()
          if (locked) return
          
          // Calculate initial offset for free drag
          const rect = gl.domElement.getBoundingClientRect()
          const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
          const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
          
          raycaster.setFromCamera({ x, y }, camera)
          const ray = raycaster.ray
          const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), -position[2])
          const intersection = new THREE.Vector3()
          ray.intersectPlane(plane, intersection)
          if (intersection) {
            dragOffsetRef.current.set(
              position[0] - intersection.x,
              position[1] - intersection.y,
              0
            )
          }
          
          setIsDragging(true)
          setDragAxis(null)
          dragAxisRef.current = null
          wasDraggingRef.current = true
          gl.domElement.style.cursor = 'grabbing'
          if (window.orbitControlsRef) {
            window.orbitControlsRef.enabled = false
          }
          
          // Add global listeners
          window.addEventListener('pointermove', handleGlobalPointerMove)
          window.addEventListener('pointerup', handleGlobalPointerUp)
        }}
        onPointerOver={(e) => {
          e.stopPropagation()
          setIsHovered(true)
          if (!isDragging) gl.domElement.style.cursor = 'pointer'
        }}
        onPointerOut={(e) => {
          e.stopPropagation()
          setIsHovered(false)
          if (!isDragging) gl.domElement.style.cursor = 'auto'
        }}
        material={material}
      />
      
      {/* Axis arrows: hide when locked */}
      {!locked && showGizmos && (
        <>
          <AxisArrow 
            direction={new THREE.Vector3(1, 0, 0)} 
            color={isDragging && dragAxis === 'x' ? '#ffffff' : '#ff2060'} 
            scale={0.5}
            onPointerDown={(e) => handleAxisPointerDown(e, 'x')}
          />
          
          <AxisArrow 
            direction={new THREE.Vector3(0, 1, 0)} 
            color={isDragging && dragAxis === 'y' ? '#ffffff' : '#20df80'} 
            scale={0.5}
            onPointerDown={(e) => handleAxisPointerDown(e, 'y')}
          />
          
          <AxisArrow 
            direction={new THREE.Vector3(0, 0, 1)} 
            color={isDragging && dragAxis === 'z' ? '#ffffff' : '#2080ff'} 
            scale={0.5}
            onPointerDown={(e) => handleAxisPointerDown(e, 'z')}
          />
        </>
      )}
    </group>
  )
}

// HandleLine component that updates in real-time
function HandleLine({ start, end, showGizmos }) {
  const lineRef = useRef()
  
  // Create geometry once with memoized initial positions
  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry()
    const positions = new Float32Array(6) // 2 points × 3 components
    positions[0] = start[0]
    positions[1] = start[1]
    positions[2] = start[2]
    positions[3] = end[0]
    positions[4] = end[1]
    positions[5] = end[2]
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    return geo
  }, []) // Empty deps - create only once
  
  // Create material once
  const material = useMemo(() => new THREE.LineBasicMaterial({ 
    color: '#ffaa00', 
    opacity: 0.5, 
    transparent: true 
  }), [])
  
  // Cleanup geometry and material on unmount
  useEffect(() => {
    return () => {
      geometry.dispose()
      material.dispose()
    }
  }, [geometry, material])
  
  // Update geometry positions in-place when start/end change
  React.useEffect(() => {
    const positions = geometry.attributes.position
    positions.array[0] = start[0]
    positions.array[1] = start[1]
    positions.array[2] = start[2]
    positions.array[3] = end[0]
    positions.array[4] = end[1]
    positions.array[5] = end[2]
    positions.needsUpdate = true
  }, [geometry, start, end])
  
  if (!showGizmos) return null

  return (
    <line ref={lineRef} geometry={geometry} material={material} />
  )
}

// Instanced trajectory line component - single draw call for all segments
const InstancedTrajectory = React.memo(({ segments, color, animationProgressRef, animationDirectionRef }) => {
  const meshRef = useRef()
  const tempObject = useMemo(() => new THREE.Object3D(), [])
  const tempMatrix = useMemo(() => new THREE.Matrix4(), [])
  const lastVisibleCountRef = useRef(-1)
  
  // Create a single shared geometry for all instances
  const geometry = useMemo(() => {
    // Use a unit cylinder that we'll scale per-instance
    return new THREE.CylinderGeometry(0.005, 0.005, 1, 8, 1, true)
  }, [])
  
  // Create material once
  const material = useMemo(() => new THREE.MeshBasicMaterial({ color }), [color])
  
  // Cleanup geometry and material on unmount
  useEffect(() => {
    return () => {
      geometry.dispose()
      material.dispose()
    }
  }, [geometry, material])
  
  // Update instance matrices whenever segments change (position/rotation only)
  useEffect(() => {
    if (!meshRef.current || !segments.length) return
    
    const mesh = meshRef.current
    
    segments.forEach((segment, i) => {
      // Set position and rotation
      tempObject.position.copy(segment.position)
      tempObject.quaternion.copy(segment.quaternion)
      // Scale Y to match segment length (cylinder is 1 unit tall by default)
      tempObject.scale.set(1, segment.length, 1)
      tempObject.updateMatrix()
      
      mesh.setMatrixAt(i, tempObject.matrix)
    })
    
    mesh.instanceMatrix.needsUpdate = true
    
    // Reset last visible count so useFrame re-applies visibility on next tick
    lastVisibleCountRef.current = -1
  }, [segments, tempObject])
  
  // Imperatively update visibility every frame — no setState, no re-renders
  useFrame(() => {
    if (!meshRef.current || !segments.length) return
    
    const progress = animationProgressRef.current
    const direction = animationDirectionRef.current
    const totalSegments = segments.length
    
    const newVisibleCount = direction === 'grow'
      ? Math.ceil(totalSegments * progress)
      : totalSegments - Math.ceil(totalSegments * progress)
    
    // Skip if nothing changed
    if (newVisibleCount === lastVisibleCountRef.current) return
    lastVisibleCountRef.current = newVisibleCount
    
    const mesh = meshRef.current
    
    segments.forEach((segment, i) => {
      const isVisible = direction === 'grow'
        ? i < newVisibleCount
        : i >= (totalSegments - newVisibleCount)
      
      tempObject.position.copy(segment.position)
      tempObject.quaternion.copy(segment.quaternion)
      tempObject.scale.set(isVisible ? 1 : 0, isVisible ? segment.length : 0, isVisible ? 1 : 0)
      tempObject.updateMatrix()
      mesh.setMatrixAt(i, tempObject.matrix)
    })
    
    mesh.instanceMatrix.needsUpdate = true
  })
  
  // Set trajectory to layer 1 so chase camera (layer 0 only) cannot see it
  useEffect(() => {
    if (meshRef.current) {
      meshRef.current.layers.set(1)
    }
  }, [segments])
  
  if (!segments.length) return null
  
  return (
    <instancedMesh 
      ref={meshRef} 
      args={[geometry, material, segments.length]}
      frustumCulled={false}
    />
  )
})

InstancedTrajectory.displayName = 'InstancedTrajectory'

export function TrajectoryEditor({
  color = '#ff6b6b',
  pointsPerSegment = 32,
  growDuration = 2, // Duration in seconds for grow animation
  eraseDuration = 1, // Duration in seconds for erase animation (2x faster)
  onEraseComplete, // Callback when erase animation finishes
  onGrowComplete, // Callback when grow animation finishes
  showGizmos: showGizmosProp = false, // Control visibility of gizmos from parent
}) {
  const groupRef = useRef()
  const animationProgressRef = useRef(0) // ref — no re-renders during grow/erase animation
  const animationStartTimeRef = useRef(null)
  const isAnimatingRef = useRef(true)
  const animationDirectionRef = useRef('grow') // 'grow' or 'erase'
  
  // Reset animation on mount (grow animation)
  React.useEffect(() => {
    animationProgressRef.current = 0
    animationStartTimeRef.current = null
    isAnimatingRef.current = true
    animationDirectionRef.current = 'grow'
  }, [])
  
  // Expose erase function to parent
  React.useEffect(() => {
    window.trajectoryErase = () => {
      animationProgressRef.current = 0
      animationStartTimeRef.current = null
      isAnimatingRef.current = true
      animationDirectionRef.current = 'erase'
    }
    
    return () => {
      delete window.trajectoryErase
    }
  }, [])
  
  // Animate grow or erase effect
  useFrame((state) => {
    if (!isAnimatingRef.current) return
    
    if (animationStartTimeRef.current === null) {
      animationStartTimeRef.current = state.clock.elapsedTime
    }
    
    const elapsed = state.clock.elapsedTime - animationStartTimeRef.current
    // Use different duration based on animation direction
    const duration = animationDirectionRef.current === 'grow' ? growDuration : eraseDuration
    const progress = Math.min(elapsed / duration, 1)
    
    animationProgressRef.current = progress // ref — no React re-render
    
    if (progress >= 1) {
      isAnimatingRef.current = false
      
      // Notify parent when erase completes
      if (animationDirectionRef.current === 'erase' && onEraseComplete) {
        onEraseComplete()
      }
      
      // Notify parent when grow completes
      if (animationDirectionRef.current === 'grow' && onGrowComplete) {
        onGrowComplete()
      }
    }
  })
  
  const [controlPoints, setControlPoints] = useState([
    {
      name: 'Point 1',
      pos: [-0.5990537004352219, 0.7855195297516792, -0.015056705748236254],
      handleIn: [0.17231374545989778, 0.020685094624550964, -0.7698241135063516],
      handleOut: [-0.1366204869874188, -0.016400361407295172, 0.6103619012005782],
      locked: true
    },
    {
      name: 'Point 2',
      pos: [0.19920606923859396, 0.4641273684325985, 0.9631936230747897],
      handleIn: [-0.5400209792508709, 0.16869982411821652, 0.03130916282808059],
      handleOut: [0.5005732592972175, -0.15637655581245974, -0.02902207559502757],
      locked: false
    },
    {
      name: 'Point 3',
      pos: [1.1360250575041544, 0.14628628755888196, 0],
      handleIn: [-0.006225333587299708, 0.06953680113103006, 0.5573671007202332],
      handleOut: [0.007621412455356378, -0.08513096283335884, -0.6823609536846734],
      locked: false
    },
    {
      name: 'Point 4',
      pos: [-0.045605215967133295, 0, -1.1218609913715833],
      handleIn: [0.5490623052680781, 0.001104998585977096, 0],
      handleOut: [-0.5801297251004675, -0.0011675223736335547, 0],
      locked: true
    },
    {
      name: 'Point 5',
      pos: [-1.173700828296186, 0, 0.010998775134302474],
      handleIn: [0.019267985273808907, -0.009710730234103515, -0.6726912436012178],
      handleOut: [-0.01906009558740589, 0.00960595743952098, 0.6654333196568599],
      locked: true
    },
    {
      name: 'Point 6',
      pos: [1.3170889472460063, 0, 1.2632736533722495],
      handleIn: [-1.9990330967394867, 0.02952984854042162, 0.19531135426002022],
      handleOut: [2.924277012049639, -0.043197612584258326, -0.285710378875879],
      locked: true
    },
    {
      name: 'Point 7',
      pos: [1.2789711483813635, 0, -1.356448160914335],
      handleIn: [2.781139651229224, -0.0015982770829863918, 0.14831933456843155],
      handleOut: [-1.8466222815784155, 0.0010612246933642266, -0.0984811344809291],
      locked: true
    },
    {
      name: 'Point 8',
      pos: [-1.2408941292727858, 0, 0.01621809208281144],
      handleIn: [-0.04653962240511139, -0.00827200656078133, -0.8925306548351956],
      handleOut: [0.04062002289606337, 0.007219850066046529, 0.7790053670668001],
      locked: true
    },
    {
      name: 'Point 9',
      pos: [1.7492852394532346, 0, 2.4679249697712318],
      handleIn: [-2.710614835969686, 0, -0.723598180434647],
      handleOut: [1.3467833278764352, 0, 0.35952358577808863],
      locked: true
    },
    {
      name: 'Point 10',
      pos: [4.701569373495156, 0, 3.3659997767833763],
      handleIn: [-0.7723560282918651, 0, -0.6905871785094391],
      handleOut: [0.8322553791688849, 0, 0.7441450225625139],
      locked: true
    },
    {
      name: 'Point 11',
      pos: [3.676694238331191, 0.030746050111627038, 4.450018793131993],
      handleIn: [0.7713003064512374, -0.038065752961593424, 0.7443337562510185],
      handleOut: [-0.6944774711929971, 0.034274338587334724, -0.6701968357087287],
      locked: true
    },
    {
      name: 'Point 12',
      pos: [2.479538541538331, 0.2603853518843079, 1.443347661397742],
      handleIn: [0.2, -0.05617233715136877, 0.7233801520565262],
      handleOut: [-0.21042157031776762, 0.05909935695905046, -0.7610739376621987],
      locked: true
    },
    {
      name: 'Point 13',
      pos: [0.9342015624483669, 0.5980233353682007, -0.8726750112293497],
      handleIn: [0.7835577501344905, -0.07178613322595573, 0.3090824118679104],
      handleOut: [-0.5289469402699052, 0.048459804675736635, -0.20864855975288688],
      locked: true
    },
    {
      name: 'Point 14',
      pos: [-0.5077051140903899, 0.692249269791709, -0.47785596292875454],
      handleIn: [0.21421220640607774, 0, -0.4500981099257835],
      handleOut: [-0.2, 0, 0.420235725570691],
      locked: true
    }
  ])
  const [showGizmos, setShowGizmos] = useState(showGizmosProp)
  
  // Update showGizmos when prop changes
  React.useEffect(() => {
    setShowGizmos(showGizmosProp)
  }, [showGizmosProp])
  
  const [history, setHistory] = useState([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const isUndoingRef = useRef(false)
  const controlPointsRef = useRef(controlPoints)
  
  // Keep ref in sync with state
  React.useEffect(() => {
    controlPointsRef.current = controlPoints
  }, [controlPoints])
  
  // Helper to save current state to history
  const saveToHistory = React.useCallback((points) => {
    if (isUndoingRef.current) return
    
    setHistory(prev => {
      // Check if state actually changed before saving
      const lastEntry = prev[historyIndex]
      if (lastEntry && JSON.stringify(lastEntry) === JSON.stringify(points)) {
        return prev // No change, don't save duplicate
      }
      
      const newHistory = prev.slice(0, historyIndex + 1)
      newHistory.push(JSON.parse(JSON.stringify(points)))
      if (newHistory.length > 50) newHistory.shift()
      return newHistory
    })
    setHistoryIndex(prev => {
      const lastEntry = history[prev]
      if (lastEntry && JSON.stringify(lastEntry) === JSON.stringify(points)) {
        return prev // Don't increment if no change
      }
      return Math.min(prev + 1, 49)
    })
  }, [history, historyIndex])
  
  // Undo function
  const handleUndo = React.useCallback(() => {
    if (historyIndex > 0) {
      isUndoingRef.current = true
      const previousState = history[historyIndex - 1]
      setControlPoints(JSON.parse(JSON.stringify(previousState)))
      setHistoryIndex(prev => prev - 1)
      setTimeout(() => { isUndoingRef.current = false }, 0)
    }
  }, [history, historyIndex])
  
  // Save initial state to history
  React.useEffect(() => {
    if (history.length === 0) {
      setHistory([JSON.parse(JSON.stringify(controlPoints))])
      setHistoryIndex(0)
    }
  }, [])
  
  // Keyboard listener for Ctrl/Cmd+Z
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleUndo])
  
  const handlePointDrag = (index, newPosition) => {
    const newPoints = [...controlPoints]
    newPoints[index] = { ...newPoints[index], pos: newPosition }
    setControlPoints(newPoints)
  }
  
  const handlePointDragEnd = (index) => {
    saveToHistory(controlPointsRef.current)
  }
  
  const handleNameChange = (index, newName) => {
    const newPoints = [...controlPoints]
    newPoints[index] = { ...newPoints[index], name: newName }
    setControlPoints(newPoints)
    saveToHistory(newPoints)
  }
  
  const handleInDrag = (index, handleType, newHandlePos) => {
    const newPoints = [...controlPoints]
    newPoints[index] = { ...newPoints[index], handleIn: newHandlePos }
    setControlPoints(newPoints)
  }
  
  const handleInDragEnd = (index) => {
    saveToHistory(controlPointsRef.current)
  }
  
  const handleOutDrag = (index, handleType, newHandlePos) => {
    const newPoints = [...controlPoints]
    newPoints[index] = { ...newPoints[index], handleOut: newHandlePos }
    setControlPoints(newPoints)
  }
  
  const handleOutDragEnd = (index) => {
    saveToHistory(controlPointsRef.current)
  }
  
  const handleAddPoint = () => {
    const lastPoint = controlPoints[controlPoints.length - 1]
    const newPoint = { 
      name: `Point ${controlPoints.length + 1}`,
      pos: [lastPoint.pos[0] + 0.5, lastPoint.pos[1], lastPoint.pos[2]],
      handleIn: [0.2, 0, 0],
      handleOut: [-0.2, 0, 0]
      , locked: false
    }
    const newPoints = [...controlPoints, newPoint]
    setControlPoints(newPoints)
    saveToHistory(newPoints)
  }

  const handleToggleLock = (index) => {
    const newPoints = [...controlPoints]
    newPoints[index] = { ...newPoints[index], locked: !newPoints[index].locked }
    setControlPoints(newPoints)
    saveToHistory(newPoints)
  }
  
  const handleDeletePoint = (index) => {
    if (controlPoints.length <= 2) {
      alert('Cannot delete - minimum 2 points required for trajectory')
      return
    }
    const newPoints = controlPoints.filter((_, i) => i !== index)
    setControlPoints(newPoints)
    saveToHistory(newPoints)
  }
  
  const handleSavePoints = () => {
    const pointsJSON = JSON.stringify(controlPoints, null, 2)
    navigator.clipboard.writeText(pointsJSON)
    console.log('Control Points copied to clipboard:', pointsJSON)
    alert('Control points (with handles) copied to clipboard!')
  }
  
  // Create curve points using Bézier-style tangent handles
  const curvePoints = useMemo(() => {
    const allPoints = []
    
    for (let i = 0; i < controlPoints.length - 1; i++) {
      const p0 = controlPoints[i]
      const p1 = controlPoints[i + 1]
      
      // Anchor points
      const anchor0 = new THREE.Vector3(...p0.pos)
      const anchor1 = new THREE.Vector3(...p1.pos)
      
      // Handle positions (absolute world space)
      const handle0Out = new THREE.Vector3(...p0.pos).add(new THREE.Vector3(...p0.handleOut))
      const handle1In = new THREE.Vector3(...p1.pos).add(new THREE.Vector3(...p1.handleIn))
      
      // Create cubic Bézier curve segment
      const curve = new THREE.CubicBezierCurve3(
        anchor0,
        handle0Out,
        handle1In,
        anchor1
      )
      
      const points = curve.getPoints(pointsPerSegment)
      allPoints.push(...points)
    }
    
    return allPoints
  }, [controlPoints, pointsPerSegment])
  
  // Pre-generate ALL cylinder data once (no dynamic changes during animation)
  const allCylinderData = useMemo(() => {
    if (curvePoints.length < 2) return []
    
    const segments = []
    const radius = 0.005 // Cylinder radius
    
    for (let i = 0; i < curvePoints.length - 1; i++) {
      const p1 = curvePoints[i]
      const p2 = curvePoints[i + 1]
      
      const midpoint = new THREE.Vector3()
        .addVectors(p1, p2)
        .multiplyScalar(0.5)
      
      const direction = new THREE.Vector3().subVectors(p2, p1)
      const length = direction.length()
      
      // Only include segments with valid length
      if (length > 0.001 && isFinite(length)) {
        const quaternion = new THREE.Quaternion()
        quaternion.setFromUnitVectors(
          new THREE.Vector3(0, 1, 0),
          direction.normalize()
        )
        
        segments.push({
          position: midpoint,
          quaternion: quaternion,
          length: length,
          radius: radius,
          index: i
        })
      }
    }
    
    return segments
  }, [curvePoints])
  
  // Calculate which segments should be visible based on animation
  // NOTE: Now handled imperatively inside InstancedTrajectory's useFrame — no useMemo needed
  
  // Store control panel functions in window for external UI access
  React.useEffect(() => {
    window.trajectoryEditorControls = {
      points: controlPoints,
      addPoint: handleAddPoint,
      deletePoint: handleDeletePoint,
      savePoints: handleSavePoints,
      updateName: handleNameChange,
      toggleLock: handleToggleLock,
      undo: handleUndo,
      canUndo: historyIndex > 0,
      showGizmos,
      toggleGizmos: () => setShowGizmos((v) => !v),
    }
    
    // Cleanup window reference on unmount
    return () => {
      delete window.trajectoryEditorControls
    }
  }, [controlPoints, historyIndex, showGizmos])
  
  // NOTE: Layer setting removed - gizmos need to stay on layer 0 for interaction
  // The InstancedTrajectory handles its own layer setting
  
  return (
    <group ref={groupRef}>
      {/* Instanced trajectory line - single draw call for all segments */}
      <InstancedTrajectory
        segments={allCylinderData}
        color={color}
        animationProgressRef={animationProgressRef}
        animationDirectionRef={animationDirectionRef}
      />
      
      {/* Control points and their handles */}
      {controlPoints.map((point, index) => (
        <React.Fragment key={`point-${index}`}>
          {/* Main control point */}
          <DraggablePoint
            position={point.pos}
            index={index}
            onDrag={handlePointDrag}
            onDragEnd={handlePointDragEnd}
            color="#00ff88"
            locked={point.locked}
            showGizmos={showGizmos}
          />
          
          {/* Handle lines */}
          <HandleLine 
            start={point.pos}
            end={[
              point.pos[0] + point.handleIn[0],
              point.pos[1] + point.handleIn[1],
              point.pos[2] + point.handleIn[2]
            ]}
            showGizmos={showGizmos}
          />
          
          <HandleLine 
            start={point.pos}
            end={[
              point.pos[0] + point.handleOut[0],
              point.pos[1] + point.handleOut[1],
              point.pos[2] + point.handleOut[2]
            ]}
            showGizmos={showGizmos}
          />
          
          {/* Draggable handles */}
          <DraggableHandle
            position={point.handleIn}
            controlPointPos={point.pos}
            index={index}
            handleType="in"
            onDrag={handleInDrag}
            onDragEnd={handleInDragEnd}
            color="#ffaa00"
            locked={point.locked}
            showGizmos={showGizmos}
          />
          
          <DraggableHandle
            position={point.handleOut}
            controlPointPos={point.pos}
            index={index}
            handleType="out"
            onDrag={handleOutDrag}
            onDragEnd={handleOutDragEnd}
            color="#ff6600"
            locked={point.locked}
            showGizmos={showGizmos}
          />
        </React.Fragment>
      ))}
    </group>
  )
}

// Separate UI component to render outside Canvas
export function TrajectoryEditorUI() {
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)
  
  // Re-render when control points change
  React.useEffect(() => {
    const interval = setInterval(forceUpdate, 100)
    return () => clearInterval(interval)
  }, [])
  
  const controls = window.trajectoryEditorControls
  if (!controls) return null
  
  return (
    <div style={{
      position: 'fixed',
      top: '12px',
      right: '12px',
      height: 'calc(100vh - 24px)',
      width: '360px',
      background: 'rgba(0, 0, 0, 0.95)',
      color: '#00ff88',
      padding: '20px',
      borderRadius: '8px',
      fontFamily: 'monospace',
      fontSize: '12px',
      display: 'flex',
      flexDirection: 'column',
      border: '1px solid #00ff88',
      zIndex: 99999,
      pointerEvents: 'auto',
      boxSizing: 'border-box'
    }}>
      <h3 style={{ margin: '0 0 15px 0', color: '#00ff88', fontSize: '14px' }}>
        Trajectory Editor
      </h3>
      
      <div style={{ marginBottom: '15px' }}>
        <button
          onClick={controls.addPoint}
          style={{
            background: '#00ff88',
            color: '#000',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '8px',
            fontFamily: 'monospace',
            fontWeight: 'bold',
          }}
        >
          + Add Point
        </button>
        
        <button
          onClick={controls.undo}
          disabled={!controls.canUndo}
          style={{
            background: controls.canUndo ? '#ffaa00' : '#333',
            color: controls.canUndo ? '#000' : '#666',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: controls.canUndo ? 'pointer' : 'not-allowed',
            marginRight: '8px',
            fontFamily: 'monospace',
            fontWeight: 'bold',
          }}
        >
          ↶ Undo
        </button>
        
        <button
          onClick={controls.savePoints}
          style={{
            background: '#2080ff',
            color: '#fff',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontFamily: 'monospace',
            fontWeight: 'bold',
            marginRight: '8px'
          }}
        >
          💾 Save to Clipboard
        </button>

        <button
          onClick={() => controls.toggleGizmos && controls.toggleGizmos()}
          style={{
            background: controls.showGizmos ? '#ffd700' : '#333',
            color: controls.showGizmos ? '#000' : '#999',
            border: 'none',
            padding: '8px 12px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontFamily: 'monospace',
            fontWeight: 'bold',
          }}
        >
          {controls.showGizmos ? 'Hide Gizmos' : 'Show Gizmos'}
        </button>
      </div>
      
      <div style={{ 
        flex: 1,
        overflowY: 'auto',
        borderTop: '1px solid #00ff88',
        paddingTop: '10px',
      }}>
        {controls.points.map((point, index) => (
          <div
            key={index}
            style={{
              marginBottom: '10px',
              padding: '10px',
              background: 'rgba(0, 255, 136, 0.1)',
              borderRadius: '4px',
              border: '1px solid rgba(0, 255, 136, 0.3)',
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '5px',
            }}>
              <input
                type="text"
                value={point.name}
                onChange={(e) => controls.updateName(index, e.target.value)}
                style={{
                  background: 'rgba(0, 255, 136, 0.1)',
                  border: '1px solid rgba(0, 255, 136, 0.3)',
                  borderRadius: '3px',
                  color: '#00ff88',
                  padding: '4px 8px',
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  flex: 1,
                  marginRight: '8px',
                }}
              />
              <button
                onClick={() => controls.deletePoint(index)}
                style={{
                  background: '#ff2060',
                  color: '#fff',
                  border: 'none',
                  padding: '4px 8px',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  marginRight: '6px'
                }}
              >
                Delete
              </button>
              <button
                onClick={() => controls.toggleLock(index)}
                style={{
                  background: point.locked ? '#444' : '#ffd700',
                  color: point.locked ? '#ccc' : '#000',
                  border: 'none',
                  padding: '4px 8px',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                }}
              >
                {point.locked ? '🔒 Locked' : '🔓 Unlock'}
              </button>
            </div>
            <div style={{ fontSize: '10px', lineHeight: '1.8', opacity: 0.8 }}>
              <div>Position: ({point.pos[0].toFixed(2)}, {point.pos[1].toFixed(2)}, {point.pos[2].toFixed(2)})</div>
              <div>Handle In: ({point.handleIn[0].toFixed(2)}, {point.handleIn[1].toFixed(2)}, {point.handleIn[2].toFixed(2)})</div>
              <div>Handle Out: ({point.handleOut[0].toFixed(2)}, {point.handleOut[1].toFixed(2)}, {point.handleOut[2].toFixed(2)})</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default TrajectoryEditor